// AddressAutocomplete.tsx — VoiceSpectrum.Web.T2 · v12.9.26 (#949: `half` puts Address in one column) · v12.1.7.0 · VoiceSpectrum AI
// Google Maps-backed address typeahead. Calls the server-side proxy (/api/t2/address/*), which uses the
// platform Maps key — the key never reaches the browser, so no Google JS and no CSP change. On selecting a
// suggestion we capture the formatted address + lat/lon and mark it verified; free typing leaves it unverified.
import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';

interface Suggestion { description: string; placeId: string; }
interface Detail { formattedAddress: string; latitude: number; longitude: number; }

export interface AddressPick {
  addressText: string;
  addressLatitude: number | null;
  addressLongitude: number | null;
  addressVerified: boolean;
}

function newSession(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AddressAutocomplete({ value, verified, onPick, half }: {
  value: string;
  verified?: boolean;
  half?: boolean;   // v12.9.26 (#949): one grid column, so the field beside it is not pushed to a new row
  onPick: (p: AddressPick) => void;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<string>(newSession());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function onInput(text: string) {
    // Free typing → unverified + clear coordinates.
    onPick({ addressText: text, addressLatitude: null, addressLongitude: null, addressVerified: false });
    setError(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (text.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    timerRef.current = setTimeout(() => { void fetchSuggestions(text.trim()); }, 250);
  }

  async function fetchSuggestions(q: string) {
    try {
      const r = await api.get<Suggestion[]>(`/api/t2/address/autocomplete?q=${encodeURIComponent(q)}&session=${sessionRef.current}`);
      setSuggestions(r); setOpen(r.length > 0);
    } catch (e) {
      setSuggestions([]); setOpen(false);
      setError(e instanceof Error ? e.message : 'Address lookup is unavailable.');
    }
  }

  async function pick(s: Suggestion) {
    setOpen(false);
    try {
      const d = await api.get<Detail>(`/api/t2/address/details?placeId=${encodeURIComponent(s.placeId)}&session=${sessionRef.current}`);
      onPick({ addressText: d.formattedAddress, addressLatitude: d.latitude, addressLongitude: d.longitude, addressVerified: true });
    } catch {
      onPick({ addressText: s.description, addressLatitude: null, addressLongitude: null, addressVerified: false });
    }
    sessionRef.current = newSession(); // fresh Places session token after a selection
  }

  return (
    <div className={half ? 'settings-field' : 'settings-field full'} ref={boxRef} style={{ position: 'relative' }}>
      <span>Address <em className="settings-hint">start typing, choose a suggestion</em>
        {verified && <em style={{ color: '#15803d', fontStyle: 'normal', fontWeight: 600, marginLeft: 6 }}>✓ verified</em>}
      </span>
      <input
        placeholder="Search address"
        value={value}
        onChange={(e) => onInput(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        autoComplete="off"
      />
      {error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{error}</div>}
      {open && suggestions.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, margin: '4px 0 0', padding: 0,
          listStyle: 'none', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 10px 30px -12px rgba(15,23,42,.25)', maxHeight: 260, overflowY: 'auto',
        }}>
          {suggestions.map((s) => (
            <li key={s.placeId}
              onMouseDown={(e) => { e.preventDefault(); void pick(s); }}
              style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
            >{s.description}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
